/**
 * API Error Handler Utility
 *
 * Provides helper functions to extract meaningful error messages
 * from various API error response formats
 */

/**
 * Backend Error Response Structure
 */
interface BackendErrorResponse {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  timestamp?: string;
  path?: string;
  method?: string;
}

/**
 * Extract error message from backend error response
 *
 * Handles multiple error formats:
 * - { message: "string" } -> "string"
 * - { message: ["error1", "error2"] } -> "error1, error2"
 * - { error: "Bad Request" } -> "Bad Request"
 * - { statusCode: 400 } -> "Request failed with status 400"
 * - {} -> fallback message
 *
 * @param error - Unknown error object from API response
 * @param fallback - Default message if no error message found
 * @returns Extracted error message
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  // Not an object, return fallback
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const errorObj = error as BackendErrorResponse;

  // Case 1: message is a string
  if (typeof errorObj.message === 'string' && errorObj.message.trim()) {
    return errorObj.message;
  }

  // Case 2: message is an array of strings (validation errors)
  if (Array.isArray(errorObj.message)) {
    const messages = errorObj.message
      .filter((msg) => typeof msg === 'string' && msg.trim())
      .join(', ');
    if (messages) {
      return messages;
    }
  }

  // Case 3: error field exists (NestJS HTTP exception)
  if (typeof errorObj.error === 'string' && errorObj.error.trim()) {
    return errorObj.error;
  }

  // Case 4: statusCode exists but no message
  if (typeof errorObj.statusCode === 'number') {
    return `Request failed with status ${errorObj.statusCode}`;
  }

  // Case 5: Unknown error structure
  return fallback;
}

/**
 * Handle API response error
 *
 * Parses response JSON and extracts error message
 *
 * @param response - Fetch Response object
 * @param fallback - Default error message
 * @returns Error with extracted message
 */
export async function handleApiError(
  response: Response,
  fallback: string,
): Promise<Error> {
  const error = await response.json().catch(() => ({}));
  const message = extractErrorMessage(error, fallback);
  return new Error(message);
}
